import moment from 'moment'
import { combineEpics } from 'redux-observable'
import { Observable } from 'rxjs/Observable'

import { CAMPAIGN_TYPES_VALUES, DELIVERY } from 'containers/AwarenessCenter/constants'
import { shareReset } from 'containers/Share/actions'
import { addToast } from 'containers/Toasts/actions'

import {
  approveCampaignError,
  approveCampaignSuccess,
  removeDraftError,
  removeDraftSuccess,
  saveAndApproveCampaignError,
  saveAndApproveCampaignSuccess,
  saveAndTestCampaignError,
  saveAndTestCampaignSuccess,
  saveDraftError,
  saveDraftSuccess,
} from '../steps/actions'
import {
  REMOVE_DRAFT_ASYNC,
  SAVE_AND_APPROVE_CAMPAIGN_ASYNC,
  SAVE_AND_TEST_CAMPAIGN_ASYNC,
  SAVE_DRAFT_ASYNC,
} from '../steps/constants'
import { sendTryItOutError, sendTryItOutSuccess } from '../steps/ReviewAndLaunchStep/actions'
import {
  selectManagerNotificationBannerContent,
  selectManagerReportEmailContent,
  selectManagerReportEmailContentSubject,
} from '../steps/selectors'
import { selectSimulationSendingRemindersCount } from '../steps/SetupStep/selectors'
import { AUTOMATED_LEVEL_BASED_TRAINING } from '../steps/TrainingModuleStep/constants'
import { MAX_TRAINING_REMINDERS } from '../WizardSimulationCampaign/constants'

const mapSaveSuccessCallbacks = {
  [SAVE_DRAFT_ASYNC.INITIATE]: saveDraftSuccess,
  [SAVE_AND_APPROVE_CAMPAIGN_ASYNC.INITIATE]: saveAndApproveCampaignSuccess,
  [SAVE_AND_TEST_CAMPAIGN_ASYNC.INITIATE]: saveAndTestCampaignSuccess,
}
const mapSaveErrorCallbacks = {
  [SAVE_DRAFT_ASYNC.INITIATE]: saveDraftError,
  [SAVE_AND_APPROVE_CAMPAIGN_ASYNC.INITIATE]: saveAndApproveCampaignError,
  [SAVE_AND_TEST_CAMPAIGN_ASYNC.INITIATE]: saveAndTestCampaignError,
}

const removeDraftEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$
    .ofType(REMOVE_DRAFT_ASYNC.INITIATE)
    .filter(() => {
      if (store.getState().awarenessCenter.wizardTrainingCampaign) return true
      return false
    })
    .mergeMap(() =>
      api.awarenessCenter
        .deleteCampaign(store.getState().awarenessCenter.wizardTrainingCampaign.self.campaign.id)
        .mergeMap(({ response }) => Observable.of(removeDraftSuccess(response)))
        .catch((error) => Observable.of(epicsErrorHandler(removeDraftError, error)))
        .takeUntil(action$.ofType(REMOVE_DRAFT_ASYNC.CANCEL))
    )

const saveDraftEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$
    .ofType(
      SAVE_DRAFT_ASYNC.INITIATE,
      SAVE_AND_APPROVE_CAMPAIGN_ASYNC.INITIATE,
      SAVE_AND_TEST_CAMPAIGN_ASYNC.INITIATE
    )
    .filter(() => {
      if (store.getState().awarenessCenter.wizardTrainingCampaign) return true
      return false
    })
    // prevent executing if user left the 'awarenessCenter' async reducer
    .filter(() => !!store.getState().awarenessCenter?.wizardTrainingCampaign)
    .mergeMap((action) => {
      const campaignData = store.getState().awarenessCenter.wizardTrainingCampaign.self.campaign
      const trainingIds = campaignData.selectedTrainingVideo.reduce((acc, { id }) => {
        if (id !== AUTOMATED_LEVEL_BASED_TRAINING.id) {
          acc.push(id)
        }

        return acc
      }, [])

      const sendingRemindersCount = selectSimulationSendingRemindersCount(store.getState())
      const remindersCount =
        sendingRemindersCount > MAX_TRAINING_REMINDERS
          ? MAX_TRAINING_REMINDERS
          : sendingRemindersCount
      const trainingReminders = remindersCount
        ? Array(remindersCount)
            .fill(null)
            .map((_, index) => campaignData.trainingReminders[index])
        : []
      const managerNotificationBanner = selectManagerNotificationBannerContent(store.getState())
        ? {
            text: selectManagerNotificationBannerContent(store.getState()),
          }
        : null
      const managerReportEmail = selectManagerReportEmailContentSubject(store.getState())
        ? {
            subject: selectManagerReportEmailContentSubject(store.getState()),
            body: selectManagerReportEmailContent(store.getState()),
          }
        : null

      return api.wizardCampaign
        .saveCampaign(store.getState().partnerOptions.selectedCompany?.value, {
          campaignId: campaignData.id,
          flowType: CAMPAIGN_TYPES_VALUES.TRAINING,
          name: campaignData.name,
          defaultLocaleId: campaignData.defaultLocaleId,
          participantsChoices: campaignData.participants.included,
          participantsExcludeChoices: campaignData.participants.excluded,
          timezone: campaignData.timezone,
          scheduleTime: moment(`${campaignData.launchDate} ${campaignData.launchTime}`).format(
            'YYYY-MM-DDTHH:mm'
          ),
          trainingIds,
          deliveryType: DELIVERY.EMAIL,
          trainingCampaignData: campaignData.messageStep.fields,
          sendReminderEveryXBusinessDays: campaignData.sendReminderEveryXBusinessDays,
          saveMailSignature: campaignData.saveMailSignature,
          closeTime: moment(campaignData.closeTime).format('YYYY-MM-DDTHH:mm[Z]'),
          sendRemindersUntil: campaignData.sendRemindersUntil,
          campaignTemplateId: campaignData.campaignTemplateId,
          sendCampaignSummaryToCampaignReportRecipients:
            campaignData.sendCampaignSummaryToCampaignReportRecipients,
          sendCompletionEmail: campaignData.sendCompletionEmail,
          trainingReminders,
          managerReportRemindersThreshold: campaignData.managerReportRemindersThreshold,
          sendManagerReportEveryXDays: campaignData.sendManagerReportEveryXDays,
          managerReportDayOfWeek: campaignData.managerReportDayOfWeek,
          managerNotificationBanner,
          managerReportEmail,
        })
        .mergeMap(({ response }) =>
          Observable.of(mapSaveSuccessCallbacks[action.type](response, action))
        )
        .catch((error) =>
          Observable.of(epicsErrorHandler(mapSaveErrorCallbacks[action.type], error))
        )
        .takeUntil(action$.ofType(SAVE_DRAFT_ASYNC.CANCEL))
    })

const approveCampaignEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$
    .ofType(SAVE_AND_APPROVE_CAMPAIGN_ASYNC.SUCCESS)
    .filter(() => {
      if (store.getState().awarenessCenter.wizardTrainingCampaign) return true
      return false
    })
    .mergeMap(() =>
      api.wizardCampaign
        .approveCampaign(store.getState().awarenessCenter.wizardTrainingCampaign.self.campaign.id)
        .mergeMap(({ response }) => Observable.of(approveCampaignSuccess(response)))
        .catch((error) => Observable.of(epicsErrorHandler(approveCampaignError, error)))
        .takeUntil(action$.ofType(SAVE_DRAFT_ASYNC.CANCEL))
    )

const sendTryItOutEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$
    .ofType(SAVE_AND_TEST_CAMPAIGN_ASYNC.SUCCESS)
    .filter(() => {
      if (store.getState().awarenessCenter.wizardTrainingCampaign) return true
      return false
    })
    .mergeMap((action) =>
      api.wizardCampaign
        .postTryItOut(
          action.payload.initialAction.payload.profileIds,
          store.getState().awarenessCenter.wizardTrainingCampaign.self.campaign.id,
          store
            .getState()
            .awarenessCenter.wizardTrainingCampaign.reviewAndLaunchStep.testSelectedScenarios.map(
              (e) => e.value
            )
        )
        .mergeMap(({ response }) =>
          Observable.of(
            sendTryItOutSuccess(response),
            addToast({ message: 'Test campaign was sent successfully!' }),
            shareReset()
          )
        )
        .catch((error) => Observable.of(epicsErrorHandler(sendTryItOutError, error)))
        .takeUntil(action$.ofType(SAVE_DRAFT_ASYNC.CANCEL))
    )

export default combineEpics(
  combineEpics(saveDraftEpic, removeDraftEpic, sendTryItOutEpic, approveCampaignEpic)
)
